import React from 'react'
import { Button, Input, Modal, Typography } from 'antd'
import { AxiosError } from 'axios'
import {
  IConfirmOwnershipTransferRequest,
  IOwnershipCodesResponse
} from 'models/sellerEmployees'
import { showServerError } from 'shared/utils/showServerError'
import styles from './OwnershipTransferModal.module.scss'

const CODE_LENGTH = 4
const RESEND_AFTER_SEC = 60

interface IOwnershipTransferModalProps {
  open: boolean
  title: string
  confirmText?: string
  failText?: string
  // последствия передачи — показываем до отправки кодов
  warning: React.ReactNode
  requestCodes: () => Promise<IOwnershipCodesResponse>
  confirmCodes: (codes: IConfirmOwnershipTransferRequest) => Promise<void>
  onClose: () => void
}

type TContacts = Pick<IOwnershipCodesResponse, 'phone' | 'email'>

const getErrorMessage = (e: unknown) =>
  (e as AxiosError<{ message?: string }>).response?.data?.message

const onlyDigits = (value: string) =>
  value.replace(/\D/g, '').slice(0, CODE_LENGTH)

export const OwnershipTransferModal: React.FC<IOwnershipTransferModalProps> = ({
  open,
  title,
  confirmText = 'Передать владение',
  failText = 'Не удалось передать владение',
  warning,
  requestCodes,
  confirmCodes,
  onClose
}) => {
  const [contacts, setContacts] = React.useState<TContacts | null>(null)
  const [smsCode, setSmsCode] = React.useState('')
  const [emailCode, setEmailCode] = React.useState('')
  const [error, setError] = React.useState('')
  const [isRequesting, setIsRequesting] = React.useState(false)
  const [isConfirming, setIsConfirming] = React.useState(false)
  const [resendAt, setResendAt] = React.useState(0)
  const [timer, setTimer] = React.useState(0)
  const isCodeStep = Boolean(contacts)

  React.useEffect(() => {
    if (!open) return
    setContacts(null)
    setSmsCode('')
    setEmailCode('')
    setError('')
  }, [open])

  React.useEffect(() => {
    if (!isCodeStep) return

    const tick = () =>
      setTimer(Math.max(Math.ceil((resendAt - Date.now()) / 1000), 0))
    tick()
    const interval = setInterval(tick, 1000)
    return () => clearInterval(interval)
  }, [isCodeStep, resendAt])

  const sendCodes = async () => {
    try {
      setIsRequesting(true)
      const data = await requestCodes()
      setContacts({ phone: data.phone, email: data.email })
      setResendAt(Date.now() + data.resend_after_sec * 1000)
      setSmsCode('')
      setEmailCode('')
      setError('')
    } catch (e) {
      // коды уже отправлены, а вкладка об этом не знает — даём их ввести
      if (getErrorMessage(e)?.includes('секунд')) {
        setContacts((prev) => prev ?? { phone: '', email: '' })
        setResendAt(Date.now() + RESEND_AFTER_SEC * 1000)
      }
      showServerError(e)
    } finally {
      setIsRequesting(false)
    }
  }

  const confirm = async () => {
    if (smsCode.length < CODE_LENGTH || emailCode.length < CODE_LENGTH) {
      setError('Введите оба кода')
      return
    }
    try {
      setIsConfirming(true)
      setError('')
      await confirmCodes({ sms_code: smsCode, email_code: emailCode })
    } catch (e) {
      const message = getErrorMessage(e)
      setError(message ?? failText)
      // после пятой ошибки и по истечении срока коды стёрты: старые вводить бессмысленно
      if (message?.includes('Запросите')) {
        setSmsCode('')
        setEmailCode('')
      }
    } finally {
      setIsConfirming(false)
    }
  }

  const footer = [
    <Button key='cancel' onClick={onClose}>
      Отмена
    </Button>,
    isCodeStep ? (
      <Button
        key='confirm'
        danger
        type='primary'
        loading={isConfirming}
        onClick={confirm}
      >
        {confirmText}
      </Button>
    ) : (
      <Button
        key='request'
        type='primary'
        loading={isRequesting}
        onClick={sendCodes}
      >
        Получить коды
      </Button>
    )
  ]

  return (
    <Modal
      centered
      open={open}
      title={title}
      footer={footer}
      onCancel={onClose}
    >
      {isCodeStep ? (
        <div className={styles.content}>
          <Typography.Text>
            Коды отправлены вам по SMS и на почту. Они действуют 10 минут.
          </Typography.Text>
          <label className={styles.codeField}>
            <span>
              Код из SMS{contacts?.phone ? ` на номер ${contacts.phone}` : ''}
            </span>
            <Input
              size='large'
              value={smsCode}
              maxLength={CODE_LENGTH}
              inputMode='numeric'
              autoComplete='one-time-code'
              disabled={isConfirming}
              onChange={(e) => setSmsCode(onlyDigits(e.target.value))}
            />
          </label>
          <label className={styles.codeField}>
            <span>
              Код из письма{contacts?.email ? ` на ${contacts.email}` : ''}
            </span>
            <Input
              size='large'
              value={emailCode}
              maxLength={CODE_LENGTH}
              inputMode='numeric'
              autoComplete='off'
              disabled={isConfirming}
              onChange={(e) => setEmailCode(onlyDigits(e.target.value))}
              onPressEnter={confirm}
            />
          </label>
          {error && <Typography.Text type='danger'>{error}</Typography.Text>}
          <Typography.Link
            disabled={timer > 0 || isRequesting}
            onClick={sendCodes}
          >
            {timer > 0
              ? `Отправить коды повторно можно через ${timer} сек`
              : 'Отправить коды повторно'}
          </Typography.Link>
        </div>
      ) : (
        <div className={styles.content}>{warning}</div>
      )}
    </Modal>
  )
}
